import { supabase } from '@/integrations/supabase/client';
import { isValidUuid } from './imageUtils';
import { extractPathFromUrl } from './imageUtils';
import { ESCORT_PHOTOS_BUCKET } from './storageService';
import { deleteStorageFile } from './storageService';
import { 
  getFromMainImageCache, 
  setMainImageCache, 
  clearMainImageCache,
  isProcessedNonUuid,
  markAsProcessedNonUuid
} from './cacheService';

// Définir le type basé sur la structure de la table vue dans supabase/types.ts
export interface EscortPhoto {
  id: string;
  escort_id: string;
  photo_url: string;
  is_main: boolean;
  is_private: boolean;
  created_at: string;
  updated_at: string;
  storage_path: string | null;
}

/**
 * Store image URL in database
 */
export const saveProfileImageToDatabase = async (
  profileId: string, 
  imageUrl: string, 
  isMain: boolean = false
): Promise<void> => {
  try {
    // Check if profile ID is a UUID
    if (!isValidUuid(profileId)) {
      throw new Error(`Cannot save image to database: ${profileId} is not a valid UUID`);
    }
    
    console.log(`Saving image to database for profile ${profileId}, URL: ${imageUrl}, isMain: ${isMain}`);
    
    // If setting this image as main, update existing main images
    if (isMain) {
      const { error: updateError } = await supabase
        .from('escort_photos')
        .update({ is_main: false })
        .eq('escort_id', profileId);
      
      if (updateError) {
        console.error('Error updating existing main images:', updateError);
        throw updateError;
      }
      
      console.log(`Existing main images updated for profile ${profileId}`);
    }
    
    // Insert new image record
    const { error, data } = await supabase
      .from('escort_photos')
      .insert({
        escort_id: profileId,
        photo_url: imageUrl,
        is_main: isMain,
        is_private: false
      })
      .select();
    
    if (error) {
      console.error('Error saving image to database:', error);
      throw error;
    }
    
    console.log(`Image saved to database for profile ${profileId}, set as main: ${isMain}`, data);
    
    // Update the main_photo_id field if this is the main photo
    if (isMain && data && data.length > 0) {
      const photoId = data[0].id;
      const { error: updateError } = await supabase
        .from('escort_profiles')
        .update({ main_photo_id: photoId })
        .eq('id', profileId);
        
      if (updateError) {
        console.error('Error updating main_photo_id:', updateError);
      } else {
        console.log(`Updated main_photo_id for profile ${profileId} to ${photoId}`);
      }
    }
    
    // Update cache with new image if it's the main one
    if (isMain) {
      setMainImageCache(profileId, imageUrl);
    }
  } catch (error) {
    console.error('Failed to save image to database:', error);
    throw error;
  }
};

/**
 * Get all images for a profile
 */
export const getProfileImages = async (profileId: string): Promise<string[]> => {
  try {
    // Check if profile ID is a UUID
    if (!isValidUuid(profileId)) {
      console.log(`Cannot get profile images: ${profileId} is not a valid UUID`);
      return [];
    }
    
    console.log(`Fetching images for profile ${profileId}`);
    
    // Check if this profile exists in Supabase for better error message
    const { data: profileExists, error: profileError } = await supabase
      .from('escort_profiles')
      .select('id')
      .eq('id', profileId)
      .maybeSingle();
    
    if (profileError) {
      console.error('Error checking if profile exists:', profileError);
    } else if (!profileExists) {
      console.log(`Profile ${profileId} not found in Supabase`);
      return [];
    }
    
    const { data, error } = await supabase
      .from('escort_photos')
      .select('photo_url')
      .eq('escort_id', profileId)
      .order('is_main', { ascending: false });
    
    if (error) {
      console.error('Error fetching profile images:', error);
      throw error;
    }
    
    const imageUrls = data?.map(item => item.photo_url) || [];
    console.log(`Retrieved ${imageUrls.length} images for profile ${profileId}`);
    
    return imageUrls;
  } catch (error) {
    console.error('Failed to get profile images:', error);
    return [];
  }
};

/**
 * Get main image for a profile
 */
export const getMainProfileImage = async (profileId: string): Promise<string | null> => {
  try {
    // Check if profile ID is a valid UUID
    const isUUID = isValidUuid(profileId);
    
    // Completely skip if not a UUID - return null immediately
    if (!isUUID) {
      console.log(`Non-UUID ID detected for profile ${profileId}, using fallback image`);
      
      // Add to processed set to skip future checks
      markAsProcessedNonUuid(profileId);
      
      // Cache result to avoid continuous calls
      setMainImageCache(profileId, null);
      return null;
    }
    
    // Completely skip if we've already processed this non-UUID ID
    if (isProcessedNonUuid(profileId)) {
      // Return cached result if available
      return getFromMainImageCache(profileId) || null;
    }
    
    // Check if we have a valid cached result that's not expired
    const cachedImage = getFromMainImageCache(profileId);
    if (cachedImage !== undefined) {
      return cachedImage;
    }
    
    console.log(`Fetching main image for profile ${profileId}`);
    
    // Directly fetch the main photo from escort_photos where is_main is true
    const { data, error } = await supabase
      .from('escort_photos')
      .select('photo_url')
      .eq('escort_id', profileId)
      .eq('is_main', true)
      .maybeSingle(); // Use maybeSingle as there might be 0 or 1 main photo
      
    if (error) {
      console.error('Error fetching main profile image:', error);
      setMainImageCache(profileId, null); // Cache error case
      throw error; // Re-throw? Or return null?
    }
    
    const result = data?.photo_url || null;
    
    console.log(`Main image for profile ${profileId}:`, result || 'none found');
    
    // Cache result
    setMainImageCache(profileId, result);
    
    return result;
  } catch (error) {
    console.error('Failed to get main profile image:', error);
    // Cache error case
    setMainImageCache(profileId, null);
    return null;
  }
};

/**
 * Delete an image from database and storage
 */
export const deleteProfileImage = async (profileId: string, imageUrl: string): Promise<boolean> => {
  try {
    // Check if profile ID is a UUID
    if (!isValidUuid(profileId)) {
      console.error(`Cannot delete image: ${profileId} is not a valid UUID`);
      return false;
    }
    
    // First, get the stored file path from URL
    const filePath = extractPathFromUrl(imageUrl, ESCORT_PHOTOS_BUCKET);
    
    if (!filePath) {
      console.error('Could not extract file path from URL:', imageUrl);
      return false;
    }
    
    console.log(`Deleting image for profile ${profileId}, path: ${filePath}`);
    
    // First get the photo ID before deleting
    const { data: photoData, error: photoError } = await supabase
      .from('escort_photos')
      .select('id, is_main')
      .eq('escort_id', profileId)
      .eq('photo_url', imageUrl)
      .maybeSingle();
      
    if (photoError) {
      console.error('Error getting photo record:', photoError);
      // Continue anyway to attempt deletion
    }
    
    const photoId = photoData?.id;
    const isMainPhoto = photoData?.is_main || false;
    
    // Delete from storage
    const storageDeleted = await deleteStorageFile(filePath, ESCORT_PHOTOS_BUCKET);
    
    if (!storageDeleted) {
      console.error('Error deleting image from storage');
      // Continue anyway to delete from database
    }
    
    // Delete from database
    const { error: dbError } = await supabase
      .from('escort_photos')
      .delete()
      .eq('escort_id', profileId)
      .eq('photo_url', imageUrl);
    
    if (dbError) {
      console.error('Error deleting image from database:', dbError);
      throw dbError;
    }
    
    // If it was the main photo, update the profile's main_photo_id and set a new main photo
    if (isMainPhoto) {
      // Find another photo to set as main
      const { data: nextPhoto } = await supabase
        .from('escort_photos')
        .select('id')
        .eq('escort_id', profileId)
        .limit(1)
        .maybeSingle();
        
      const newMainPhotoId = nextPhoto?.id || null;
      
      // Update the profile
      const { error: updateError } = await supabase
        .from('escort_profiles')
        .update({ main_photo_id: newMainPhotoId })
        .eq('id', profileId);
        
      if (updateError) {
        console.error('Error updating main_photo_id:', updateError);
      } else if (newMainPhotoId) {
        // Mark the new photo as main
        const { error: updateMainError } = await supabase
          .from('escort_photos')
          .update({ is_main: true })
          .eq('id', newMainPhotoId);
          
        if (updateMainError) {
          console.error('Error setting new main photo:', updateMainError);
        }
      }
    }
    
    console.log(`Successfully deleted image for profile ${profileId}`);
    
    // Clear cache for this profile
    clearMainImageCache(profileId);
    
    return true;
  } catch (error) {
    console.error('Failed to delete profile image:', error);
    return false;
  }
};

/**
 * Check if a profile exists in Supabase
 */
export const checkProfileExistsInSupabase = async (profileId: string): Promise<boolean> => {
  try {
    if (!isValidUuid(profileId)) {
      return false;
    }
    
    const { data, error } = await supabase
      .from('escort_profiles')
      .select('id')
      .eq('id', profileId)
      .maybeSingle();
    
    if (error) {
      console.error(`Error checking if profile ${profileId} exists:`, error);
      return false;
    }
    
    console.log(`Profile check for ${profileId}:`, data ? `Found: ${data.id}` : 'Not found');
    return !!data;
  } catch (error) {
    console.error(`Error in checkProfileExistsInSupabase for ${profileId}:`, error);
    return false;
  }
};

/**
 * Get escort profile details from Supabase
 */
export const getEscortProfileById = async (profileId: string) => {
  try {
    if (!isValidUuid(profileId)) {
      console.log(`ID ${profileId} is not a valid UUID for Supabase`);
      return null;
    }

    console.log(`Retrieving escort profile with ID: ${profileId}`);
    
    const { data, error } = await supabase
      .from('escort_profiles')
      .select(`
        id,
        display_name, age,
        bio_short, bio_full, city, country,
        latitude, longitude, gender, height,
        weight, body_type, hair_color, eye_color,
        ethnicity, nationality, measurements, cup_size,
        intimate_area, has_tattoos, has_piercings, smoking,
        drinking, sexual_orientation, is_verified, is_online, is_touring,
        main_photo_id, main_photo:main_photo_id(photo_url),
        escort_services (
          id, name, description, visibility
        ),
        escort_rates (
          id, description, price, currency, duration, visibility
        )
      `)
      .eq('id', profileId)
      .single();
    
    if (error) {
      console.error(`Error retrieving escort profile ${profileId}:`, error);
      throw error;
    }
    
    if (!data) {
      console.log(`No profile found with ID: ${profileId}`);
      return null;
    }
    
    console.log(`Retrieved escort profile with relationships:`, data);
    
    // Ensure main photos are properly identified using the main_photo_id field
    if (!data.main_photo_id && data.escort_photos && Array.isArray(data.escort_photos) && data.escort_photos.length > 0) {
      // If no main photo is set but there are photos, mark the first as main
      console.log(`No main photo found for ${profileId}, setting first photo as main`);
      const firstPhotoId = data.escort_photos[0].id;
      
      // Update the database to mark this photo as main
      const { error: updateError } = await supabase
        .from('escort_photos')
        .update({ is_main: true })
        .eq('id', firstPhotoId);
        
      if (updateError) {
        console.error(`Error setting main photo:`, updateError);
      } else {
        // Update the profile's main_photo_id field
        const { error: updateProfileError } = await supabase
          .from('escort_profiles')
          .update({ main_photo_id: firstPhotoId })
          .eq('id', profileId);
          
        if (updateProfileError) {
          console.error(`Error updating main_photo_id:`, updateProfileError);
        } else {
          // Update local data to reflect this change
          data.main_photo_id = firstPhotoId;
          if (Array.isArray(data.escort_photos)) {
            data.escort_photos[0].is_main = true;
          }
        }
      }
    }
    
    return data;
  } catch (error) {
    console.error(`Error in getEscortProfileById for ${profileId}:`, error);
    return null;
  }
};

/**
 * Get all escort profiles
 */
export const getEscortProfiles = async () => {
  try {
    const { data, error } = await supabase
      .from('escort_profiles')
      .select(`
        id, 
        display_name, age,
        bio_short, bio_full, city, country,
        latitude, longitude, gender, height,
        weight, body_type, hair_color, eye_color,
        ethnicity, nationality, measurements, cup_size,
        intimate_area, has_tattoos, has_piercings, smoking,
        drinking, sexual_orientation, is_verified, is_online, is_touring,
        main_photo_id, main_photo:main_photo_id(photo_url),
        escort_services (
          id, name, description, visibility
        ),
        escort_rates (
          id, description, price, currency, duration, visibility
        )
      `);

    if (error) {
      console.error('Error fetching escort profiles:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Failed to fetch escort profiles:', error);
    throw error;
  }
};

/**
 * Set a specific image as the main profile image
 */
export const setMainProfileImage = async (escortId: string, photoId: string): Promise<boolean> => {
  try {
    if (!isValidUuid(escortId) || !isValidUuid(photoId)) {
      console.error('Invalid UUID provided for escort or photo');
      return false;
    }

    console.log(`Setting photo ${photoId} as main for escort ${escortId}`);

    // Step 1: Set all other photos for this escort to is_main = false
    const { error: errorUnset } = await supabase
      .from('escort_photos')
      .update({ is_main: false })
      .eq('escort_id', escortId);

    if (errorUnset) {
      console.error('Error unsetting previous main photos:', errorUnset);
      throw errorUnset;
    }

    // Step 2: Set the selected photo to is_main = true
    const { error: errorSet } = await supabase
      .from('escort_photos')
      .update({ is_main: true })
      .eq('id', photoId)
      .eq('escort_id', escortId); // Ensure we only update the photo for the correct escort

    if (errorSet) {
      console.error('Error setting new main photo:', errorSet);
      throw errorSet;
    }
    
    // Step 3: Update the main_photo_id in the escort_profiles table
    const { error: errorUpdateProfile } = await supabase
      .from('escort_profiles')
      .update({ main_photo_id: photoId })
      .eq('id', escortId);

    if (errorUpdateProfile) {
      console.error('Error updating main_photo_id in escort_profiles:', errorUpdateProfile);
      // Depending on desired behavior, you might still return true if the photo itself was set
      // but for consistency, we treat this as a failure of the whole operation.
      throw errorUpdateProfile;
    }

    console.log(`Successfully set photo ${photoId} as main for escort ${escortId}`);
    
    // Clear specific cache if necessary (assuming getMainProfileImage might be cached)
    clearMainImageCache(escortId); 

    return true;
  } catch (error) {
    console.error('Failed to set main profile image:', error);
    return false;
  }
};

/**
 * Get full photo details for a profile
 */
export const getEscortPhotoDetails = async (profileId: string): Promise<EscortPhoto[]> => {
  try {
    if (!isValidUuid(profileId)) {
      console.log(`Cannot get photo details: ${profileId} is not a valid UUID`);
      return [];
    }
    
    console.log(`Fetching photo details for profile ${profileId}`);
    
    const { data, error } = await supabase
      .from('escort_photos')
      .select('*') // Select all columns
      .eq('escort_id', profileId)
      .order('created_at', { ascending: true }); // Order by creation date, oldest first
      
    if (error) {
      console.error('Error fetching profile photo details:', error);
      throw error;
    }
    
    console.log(`Retrieved ${data?.length || 0} photo details for profile ${profileId}`);
    return data || [];
    
  } catch (error) {
    console.error('Failed to get profile photo details:', error);
    return [];
  }
};
